// Name — the one way a rail or a party is rendered, anywhere.
//
// Every list row, picker row, card header and detail header uses this. That is
// what delivers "consistent placement of the same thing on every screen": the
// operator learns one shape and reads it everywhere.
//
// LAYOUT LAW (docs/NAMING-AND-RELEVANCE.md §4.4)
//   primary    text-sm font-medium, ONE line, truncate, full value in `title`.
//   secondary  text-meta text-tg-hint, ONE line, truncate. Omitted entirely
//              when empty — never an empty node that shifts the row. A PICKER
//              passes `wrapSecondary`: its line may take a second line instead
//              of an ellipsis, because the end of it is what he chooses by
//              ("… you hold 18,400.5 USDT" was cut to "… you hold 18,400.5 US…"
//              when the codemod moved 11px to the 12px role, 22 Oct 2026).
//   trailing   right-aligned, shrink-0, font-mono tabular-nums. The asset chip
//              and the balance live HERE and are never concatenated into
//              `primary` — that is what produced RailPicker's
//              `Wio · Shweta · USDT·TRC` mush in its selected state.
//   review     small muted flag; it sits OUTSIDE the truncating span so a long
//              name can never truncate the flag away.
//
// Row height is fixed (`min-h-[32px]`, centered) so it is identical with or
// without a secondary line. Lists therefore do not jitter as data loads, and a
// party whose subtitle is still resolving occupies the same space it will end
// up occupying.
//
// Mobile: there are NO responsive classes here on purpose. The component is
// byte-identical below `lg`; compactness is a *content* decision made upstream
// by `partySubtitle(p, { compact: true })`. One component, one height, every
// breakpoint.

import type { ReactNode } from 'react';
import type { NameReview } from '../lib/naming';

function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(' ');
}

const REVIEW_TITLE: Record<Exclude<NameReview, null>, string> = {
  profanity: 'Flagged for review — shown exactly as stored, never auto-renamed',
  ambiguous: 'Ambiguous name — needs an operator to confirm who this is',
  empty:     'No stored name — a stand-in is shown; give this one a name',
};

/** Small muted flag. Deliberately not a warning colour: this is a nudge for a
 *  later cleanup pass, not an error, and it must not pull the eye away from
 *  balances. */
function ReviewFlag({ reason }: { reason: Exclude<NameReview, null> }) {
  return (
    <span
      className="shrink-0 text-tg-hint/70 text-meta leading-none select-none"
      title={REVIEW_TITLE[reason]}
      aria-label={REVIEW_TITLE[reason]}
      role="img"
    >
      ⚑
    </span>
  );
}

export function Name({
  primary,
  secondary,
  trailing,
  review,
  className,
  wrapSecondary = false,
}: {
  /** railName() / partyName() output — never a raw slug, never empty. */
  primary: string;
  /** railSubtitle() / partySubtitle() output. Falsy ⇒ the node is not rendered. */
  secondary?: string;
  /** Asset chip, balance, star. Right-aligned and tabular. */
  trailing?: ReactNode;
  /** needsNameReview() result. `null`/undefined renders nothing. */
  review?: NameReview;
  className?: string;
  /** The secondary line may wrap to a second line (a picker's rows) instead of ending in an ellipsis. Lists leave it off. */
  wrapSecondary?: boolean;
}) {
  return (
    <div className={cx('flex items-center gap-2 min-w-0 min-h-[32px]', className)}>
      <div className="flex flex-col justify-center min-w-0 flex-1">
        <span className="flex items-center gap-1 min-w-0">
          <span
            className="text-body font-medium leading-[18px] truncate"
            title={primary}
          >
            {primary}
          </span>
          {review ? <ReviewFlag reason={review} /> : null}
        </span>

        {secondary ? (
          <span
            className={wrapSecondary ? 'text-meta leading-4 text-tg-hint line-clamp-2 break-words text-balance' : 'text-meta leading-[13px] text-tg-hint truncate'}
            title={secondary}
          >
            {secondary}
          </span>
        ) : null}
      </div>

      {trailing ? (
        <div className="shrink-0 text-right amt text-body leading-[18px]">
          {trailing}
        </div>
      ) : null}
    </div>
  );
}
